import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, Chip, Alert
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";
import { db } from "../firebase";
import {
  collection, onSnapshot, addDoc, updateDoc,
  doc, orderBy, query, getDoc, getDocs
} from "firebase/firestore";
import { useLista } from "../hooks/useListe";
import { salvaLog } from "../utils/logger";
import { useAuth } from "../context/AuthContext";

const CAMPI_VUOTI = {
  tipologiaFornitore: "",
  nominativo: "",
  specializzazione: [],
  email: "",
  telefono: "",
  sitoWeb: "",
  cittaZona: "",
  note: "",
  regoleIngaggio: "",
};

export default function Collaboratori() {
  const { currentUser } = useAuth();
  const [collaboratori, setCollaboratori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(CAMPI_VUOTI);
  const [saving, setSaving] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [importando, setImportando] = useState(false);
  const [risultatoImport, setRisultatoImport] = useState(null);

  const [eventiDialogOpen, setEventiDialogOpen] = useState(false);
  const [eventiPassatiDettaglio, setEventiPassatiDettaglio] = useState([]);
  const [loadingEventi, setLoadingEventi] = useState(false);
  const [collaboratoreSelezionato, setCollaboratoreSelezionato] = useState(null);

  const { items: tipologie, aggiungiItem: aggiungiTipologia } = useLista("tipologieFornitore");
  const [nuovaTipologia, setNuovaTipologia] = useState("");
  const [aggiungiTipologiaOpen, setAggiungiTipologiaOpen] = useState(false);

  const { items: specializzazioni, aggiungiItem: aggiungiSpecializzazione } = useLista("specializzazioni");
  const [nuovaSpecializzazione, setNuovaSpecializzazione] = useState("");
  const [aggiungiSpecializzazioneOpen, setAggiungiSpecializzazioneOpen] = useState(false);

  const [clientiMap, setClientiMap] = useState({});

  useEffect(() => {
    const q = query(collection(db, "collaboratori"), orderBy("nominativo"));
    const unsub = onSnapshot(q, (snap) => {
      setCollaboratori(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "clienti"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data().nome; });
      setClientiMap(map);
    });
    return unsub;
  }, []);

  async function apriEventiPassati(collaboratore) {
    setCollaboratoreSelezionato(collaboratore);
    setEventiDialogOpen(true);
    setLoadingEventi(true);
    try {
      const ids = collaboratore.eventiPassati || [];
      const eventi = await Promise.all(
        ids.map(async (eventoId) => {
          const snap = await getDoc(doc(db, "eventi", eventoId));
          if (snap.exists()) return { id: snap.id, ...snap.data() };
          return null;
        })
      );
      setEventiPassatiDettaglio(eventi.filter(Boolean));
    } catch (error) {
      console.error("Errore caricamento eventi passati:", error);
    }
    setLoadingEventi(false);
  }

  function apriNuovo() {
    setEditId(null);
    setForm(CAMPI_VUOTI);
    setDialogOpen(true);
  }

  function apriModifica(collaboratore) {
    setEditId(collaboratore.id);
    setForm({
      ...CAMPI_VUOTI,
      ...collaboratore,
      specializzazione: Array.isArray(collaboratore.specializzazione)
        ? collaboratore.specializzazione
        : collaboratore.specializzazione
        ? [collaboratore.specializzazione]
        : [],
    });
    setDialogOpen(true);
  }

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSalva() {
    if (!form.nominativo.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        const vecchio = collaboratori.find((c) => c.id === editId);
        await updateDoc(doc(db, "collaboratori", editId), form);
        const modifiche = [];
        const campiLabel = {
          nominativo: "Nominativo", email: "Email", telefono: "Telefono",
          cittaZona: "Città/Zona", sitoWeb: "Sito web", note: "Note",
          regoleIngaggio: "Regole di ingaggio",
        };
        for (const [campo, label] of Object.entries(campiLabel)) {
          if (vecchio?.[campo] !== form[campo]) {
            modifiche.push({ campo: label, da: vecchio?.[campo] || "—", a: form[campo] || "—" });
          }
        }
        salvaLog(currentUser, "Modifica collaboratore", "", form.nominativo, modifiche);
      } else {
        await addDoc(collection(db, "collaboratori"), { ...form, eventiPassati: [] });
        salvaLog(currentUser, "Nuovo collaboratore", "", form.nominativo, [
          { campo: "Nominativo", da: "—", a: form.nominativo }
        ]);
      }
      setDialogOpen(false);
    } catch (error) {
      console.error("Errore salvataggio collaboratore:", error);
    }
    setSaving(false);
  }

  async function handleAggiungiTipologia() {
    if (!nuovaTipologia.trim()) return;
    await aggiungiTipologia(nuovaTipologia.trim());
    setNuovaTipologia("");
    setAggiungiTipologiaOpen(false);
  }

  async function handleAggiungiSpecializzazione() {
    if (!nuovaSpecializzazione.trim()) return;
    await aggiungiSpecializzazione(nuovaSpecializzazione.trim());
    setNuovaSpecializzazione("");
    setAggiungiSpecializzazioneOpen(false);
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    setRisultatoImport(null);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const righe = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const esistentiSnap = await getDocs(collection(db, "collaboratori"));
      const nominativiEsistenti = new Set(
        esistentiSnap.docs.map((d) => d.data().nominativo?.toLowerCase()).filter(Boolean)
      );

      const tipologieSnap = await getDocs(collection(db, "tipologieFornitore"));
      const tipologieMap = {};
      tipologieSnap.docs.forEach((d) => { tipologieMap[d.data().nome?.toLowerCase()] = d.id; });

      const specializzazioniSnap = await getDocs(collection(db, "specializzazioni"));
      const specializzazioniSet = new Set(
        specializzazioniSnap.docs.map((d) => d.data().nome?.toLowerCase())
      );

      let importati = 0;
      let saltati = 0;

      const primaRiga = righe[0];
      const startIdx = primaRiga && typeof primaRiga[0] === "string" &&
        primaRiga[0].toLowerCase().includes("nom") ? 1 : 0;

      for (let i = startIdx; i < righe.length; i++) {
        const riga = righe[i];
        if (!riga || riga.length < 1) { saltati++; continue; }

        const nominativo = String(riga[0] || "").trim();
        if (!nominativo) { saltati++; continue; }
        if (nominativiEsistenti.has(nominativo.toLowerCase())) { saltati++; continue; }

        const tipologiaNome = String(riga[1] || "").trim();
        const specializzazioneNome = String(riga[2] || "").trim();
        const cittaZona = String(riga[3] || "").trim();
        const email = String(riga[4] || "").trim().toLowerCase();
        const telefono = String(riga[5] || "").trim();
        const sitoWeb = String(riga[6] || "").trim();
        const note = String(riga[7] || "").trim();
        const regoleIngaggio = String(riga[8] || "").trim();

        let tipologiaId = "";
        if (tipologiaNome) {
          tipologiaId = tipologieMap[tipologiaNome.toLowerCase()] || "";
          if (!tipologiaId) {
            const ref = await addDoc(collection(db, "tipologieFornitore"), { nome: tipologiaNome });
            tipologieMap[tipologiaNome.toLowerCase()] = ref.id;
            tipologiaId = ref.id;
          }
        }

        let specializzazioneArr = [];
        if (specializzazioneNome) {
          specializzazioneArr = [specializzazioneNome];
          if (!specializzazioniSet.has(specializzazioneNome.toLowerCase())) {
            await addDoc(collection(db, "specializzazioni"), { nome: specializzazioneNome });
            specializzazioniSet.add(specializzazioneNome.toLowerCase());
          }
        }

        await addDoc(collection(db, "collaboratori"), {
          nominativo, tipologiaFornitore: tipologiaId,
          specializzazione: specializzazioneArr, cittaZona,
          email, telefono, sitoWeb, note, regoleIngaggio, eventiPassati: [],
        });

        nominativiEsistenti.add(nominativo.toLowerCase());
        importati++;
      }

      salvaLog(currentUser, "Import collaboratori", "", "", [
        { campo: "Importati", da: "—", a: String(importati) },
        { campo: "Saltati", da: "—", a: String(saltati) },
      ]);
      setRisultatoImport({ importati, saltati });
    } catch (error) {
      console.error("Errore import:", error);
      setRisultatoImport({ errore: error.message });
    }

    setImportando(false);
    e.target.value = "";
  }

  const collaboratoriFiltrati = collaboratori.filter((c) => {
    const spec = Array.isArray(c.specializzazione)
      ? c.specializzazione.join(" ")
      : c.specializzazione || "";
    return (
      c.nominativo?.toLowerCase().includes(ricerca.toLowerCase()) ||
      spec.toLowerCase().includes(ricerca.toLowerCase()) ||
      c.cittaZona?.toLowerCase().includes(ricerca.toLowerCase())
    );
  });

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Collaboratori</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" component="label"
            startIcon={importando ? <CircularProgress size={18} /> : <UploadFileIcon />}
            disabled={importando}>
            {importando ? "Importazione..." : "Importa Excel"}
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleImport} />
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={apriNuovo}>
            Nuovo collaboratore
          </Button>
        </Box>
      </Box>

      {risultatoImport && !risultatoImport.errore && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRisultatoImport(null)}>
          Import completato — {risultatoImport.importati} collaboratori importati,{" "}
          {risultatoImport.saltati} saltati
        </Alert>
      )}
      {risultatoImport?.errore && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRisultatoImport(null)}>
          Errore: {risultatoImport.errore}
        </Alert>
      )}

      <TextField fullWidth placeholder="Cerca per nominativo, specializzazione o città..."
        value={ricerca} onChange={(e) => setRicerca(e.target.value)} sx={{ mb: 3 }} />

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
              <TableCell><strong>Nominativo</strong></TableCell>
              <TableCell><strong>Tipologia</strong></TableCell>
              <TableCell><strong>Specializzazione</strong></TableCell>
              <TableCell><strong>Città/Zona</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Telefono</strong></TableCell>
              <TableCell><strong>Eventi passati</strong></TableCell>
              <TableCell align="center"><strong>Azioni</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collaboratoriFiltrati.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Nessun collaboratore trovato
                </TableCell>
              </TableRow>
            ) : (
              collaboratoriFiltrati.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.nominativo || "—"}</TableCell>
                  <TableCell>
                    {tipologie.find((t) => t.id === c.tipologiaFornitore)?.nome || c.tipologiaFornitore || "—"}
                  </TableCell>
                  <TableCell>
                    {Array.isArray(c.specializzazione) ? c.specializzazione.join(", ") : c.specializzazione || "—"}
                  </TableCell>
                  <TableCell>{c.cittaZona || "—"}</TableCell>
                  <TableCell>{c.email || "—"}</TableCell>
                  <TableCell>{c.telefono || "—"}</TableCell>
                  <TableCell>
                    <Chip label={c.eventiPassati?.length || 0} size="small"
                      color={c.eventiPassati?.length > 0 ? "primary" : "default"}
                      onClick={() => c.eventiPassati?.length > 0 && apriEventiPassati(c)}
                      clickable={c.eventiPassati?.length > 0} />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => apriEventiPassati(c)}
                      disabled={!c.eventiPassati?.length}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => apriModifica(c)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* DIALOG EVENTI PASSATI */}
      <Dialog open={eventiDialogOpen} onClose={() => setEventiDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Eventi passati — {collaboratoreSelezionato?.nominativo}</DialogTitle>
        <DialogContent>
          {loadingEventi ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : eventiPassatiDettaglio.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>Nessun evento trovato.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                    <TableCell><strong>Titolo</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell><strong>Stato</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eventiPassatiDettaglio.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell>{e.titolo || "—"}</TableCell>
                      <TableCell>{clientiMap[e.clienteId] || "—"}</TableCell>
                      <TableCell>{e.dataInizio || e.data || "—"}</TableCell>
                      <TableCell><Chip label={e.stato} size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventiDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG NUOVO/MODIFICA */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? "Modifica collaboratore" : "Nuovo collaboratore"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nominativo" value={form.nominativo}
                onChange={(e) => handleChange("nominativo", e.target.value)} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipologia fornitore</InputLabel>
                <Select value={form.tipologiaFornitore} label="Tipologia fornitore"
                  onChange={(e) => handleChange("tipologiaFornitore", e.target.value)}>
                  {tipologie.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                  ))}
                  <MenuItem onClick={() => setAggiungiTipologiaOpen(true)}
                    sx={{ color: "primary.main", fontWeight: "bold" }}>
                    + Aggiungi nuova...
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Specializzazione</InputLabel>
                <Select multiple
                  value={Array.isArray(form.specializzazione) ? form.specializzazione : []}
                  label="Specializzazione"
                  onChange={(e) => handleChange("specializzazione", e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => <Chip key={val} label={val} size="small" />)}
                    </Box>
                  )}>
                  {specializzazioni.map((s) => (
                    <MenuItem key={s.id} value={s.nome}>{s.nome}</MenuItem>
                  ))}
                  <MenuItem onClick={(e) => { e.stopPropagation(); setAggiungiSpecializzazioneOpen(true); }}
                    sx={{ color: "primary.main", fontWeight: "bold" }}>
                    + Aggiungi nuova...
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Città e zona di riferimento" value={form.cittaZona || ""}
                onChange={(e) => handleChange("cittaZona", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Email" type="email" value={form.email}
                onChange={(e) => handleChange("email", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Telefono" value={form.telefono}
                onChange={(e) => handleChange("telefono", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Sito web" value={form.sitoWeb}
                onChange={(e) => handleChange("sitoWeb", e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Note" value={form.note}
                onChange={(e) => handleChange("note", e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Regole di ingaggio"
                value={form.regoleIngaggio}
                onChange={(e) => handleChange("regoleIngaggio", e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSalva}
            disabled={saving || !form.nominativo.trim()}>
            {saving ? <CircularProgress size={20} /> : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG NUOVA TIPOLOGIA */}
      <Dialog open={aggiungiTipologiaOpen} onClose={() => setAggiungiTipologiaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Aggiungi tipologia fornitore</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Tipologia" value={nuovaTipologia}
            onChange={(e) => setNuovaTipologia(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAggiungiTipologia()} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAggiungiTipologiaOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleAggiungiTipologia}
            disabled={!nuovaTipologia.trim()}>Aggiungi</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG NUOVA SPECIALIZZAZIONE */}
      <Dialog open={aggiungiSpecializzazioneOpen} onClose={() => setAggiungiSpecializzazioneOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Aggiungi specializzazione</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Specializzazione" value={nuovaSpecializzazione}
            onChange={(e) => setNuovaSpecializzazione(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAggiungiSpecializzazione()} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAggiungiSpecializzazioneOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleAggiungiSpecializzazione}
            disabled={!nuovaSpecializzazione.trim()}>Aggiungi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}